import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { findNearestFuelStop, getHomeFuelTanks, logFuelStop, getFuelHistory } from '../services/fuelStopService';

function FuelStopModal({ onClose }) {
  const [activeTab, setActiveTab] = useState('find');
  const [loading, setLoading] = useState(false);
  const [trucks, setTrucks] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [fuelStops, setFuelStops] = useState([]);
  const [fuelHistory, setFuelHistory] = useState([]);
  const [homeTanks, setHomeTanks] = useState([]);
  const [message, setMessage] = useState(null);
  const [currentLocation, setCurrentLocation] = useState({ lat: 53.5461, lng: -113.4938 });
  
  const [fuelRequest, setFuelRequest] = useState({
    urgency: 50,
    fuelType: 'diesel',
    estimatedLiters: 200,
    notes: ''
  });

  const [logForm, setLogForm] = useState({
    stationId: '',
    stationName: '',
    isHomeTank: false,
    fuelType: 'diesel',
    liters: '',
    pricePerLiter: '',
    odometerKm: '',
    notes: ''
  });

  useEffect(() => {
    fetchTrucks();
    fetchDrivers();
    setHomeTanks(getHomeFuelTanks());
  }, []);

  useEffect(() => {
    if (selectedTruck) {
      searchFuelStops();
      loadFuelHistory();
    }
  }, [selectedTruck, fuelRequest.fuelType]);

  const fetchTrucks = async () => {
    const { data } = await supabase
      .from('trucks')
      .select('*')
      .eq('is_active', true)
      .order('truck_number');
    if (data) {
      setTrucks(data);
      if (data.length > 0) setSelectedTruck(data[0]);
    }
  };

  const fetchDrivers = async () => {
    const { data } = await supabase
      .from('drivers')
      .select('*, user:user_id(name)')
      .order('id');
    if (data) setDrivers(data);
  };

  const searchFuelStops = async () => {
    setLoading(true);
    try {
      const stops = await findNearestFuelStop(currentLocation, fuelRequest.fuelType, true);
      setFuelStops(stops);
    } catch (error) {
      console.error('Error searching fuel stops:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFuelHistory = async () => {
    if (selectedTruck) {
      const history = await getFuelHistory(selectedTruck.id);
      setFuelHistory(history);
    }
  };

  const handleLogFuelStop = async () => {
    if (!selectedTruck || !logForm.liters || !logForm.stationName) {
      setMessage({ type: 'error', text: 'Please fill in required fields' }); return;
      return;
    }

    try {
      await logFuelStop(selectedDriver?.id, selectedTruck.id, {
        stationId: logForm.stationId,
        stationName: logForm.stationName,
        isHomeTank: logForm.isHomeTank,
        fuelType: logForm.fuelType,
        liters: parseFloat(logForm.liters),
        pricePerLiter: parseFloat(logForm.pricePerLiter) || 0,
        odometerKm: parseInt(logForm.odometerKm) || selectedTruck.current_odometer_km,
        lat: currentLocation.lat,
        lng: currentLocation.lng,
        notes: logForm.notes
      });

      setMessage({ type: 'success', text: 'Fuel stop logged!' });
      setLogForm({
        stationId: '',
        stationName: '',
        isHomeTank: false,
        fuelType: 'diesel',
        liters: '',
        pricePerLiter: '',
        odometerKm: '',
        notes: ''
      });
      loadFuelHistory();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed: ' + error.message });
    }
  };

  const selectFuelStop = (stop) => {
    setLogForm({
      ...logForm,
      stationId: stop.id,
      stationName: stop.name,
      isHomeTank: stop.isHomeTank,
      pricePerLiter: stop.pricePerLiter?.toFixed(2) || ''
    });
    setActiveTab('log');
  };

  const getUrgencyColor = (urgency) => {
    if (urgency >= 80) return 'var(--color-danger)';
    if (urgency >= 50) return 'var(--color-warning)';
    return 'var(--color-success)';
  };

  const getUrgencyLabel = (urgency) => {
    if (urgency >= 80) return 'Critical - Need fuel ASAP';
    if (urgency >= 50) return 'Medium - Can drive 50-100km';
    return 'Low - Planning ahead';
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{
        backgroundColor: 'var(--surface-1)',
        borderRadius: '10px',
        padding: '1.5rem',
        width: '100%',
        maxWidth: '900px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
          paddingBottom: '1rem',
          borderBottom: '1px solid var(--surface-2)'
        }}>
          <div>
            <h2 style={{ color: 'var(--accent-primary)', margin: '0 0 0.25rem 0' }}>
              Fuel Management
            </h2>
            <p style={{ color: 'var(--text-muted)', margin: 0, fontSize: '0.9rem' }}>
              Find fuel stops and track fuel consumption
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-primary)',
              fontSize: '1.5rem',
              cursor: 'pointer'
            }}
          >
            x
          </button>
        </div>

        {/* Truck selector */}
        <div style={{ marginBottom: '1rem' }}>
          <select
            value={selectedTruck?.id || ''}
            onChange={(e) => setSelectedTruck(trucks.find(t => t.id === e.target.value))}
            style={{
              padding: '0.5rem',
              backgroundColor: 'var(--surface-2)',
              border: '1px solid var(--border-default)',
              borderRadius: '5px',
              color: 'var(--text-primary)',
              marginRight: '0.5rem'
            }}
          >
            {trucks.map(truck => (
              <option key={truck.id} value={truck.id}>
                Truck {truck.truck_number} - {truck.fuel_type}
              </option>
            ))}
          </select>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          marginBottom: '1rem'
        }}>
          <button
            onClick={() => setActiveTab('find')}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: activeTab === 'find' ? 'var(--btn-primary-bg)' : 'var(--surface-2)',
              color: 'var(--text-primary)',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            Find Fuel
          </button>
          <button
            onClick={() => setActiveTab('log')}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: activeTab === 'log' ? 'var(--btn-primary-bg)' : 'var(--surface-2)',
              color: 'var(--text-primary)',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            Log Fuel Stop
          </button>
          <button
            onClick={() => setActiveTab('history')}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: activeTab === 'history' ? 'var(--btn-primary-bg)' : 'var(--surface-2)',
              color: 'var(--text-primary)',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            History
          </button>
          <button
            onClick={() => setActiveTab('tanks')}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: activeTab === 'tanks' ? 'var(--btn-primary-bg)' : 'var(--surface-2)',
              color: 'var(--text-primary)',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            Home Tanks
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          {activeTab === 'find' ? (
            <div>
              {/* Urgency slider */}
              <div style={{
                backgroundColor: 'var(--surface-2)',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ color: 'var(--text-primary)' }}>Fuel Urgency</span>
                  <span style={{ color: getUrgencyColor(fuelRequest.urgency), fontWeight: 'bold' }}>
                    {getUrgencyLabel(fuelRequest.urgency)}
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={fuelRequest.urgency}
                  onChange={(e) => setFuelRequest({ ...fuelRequest, urgency: parseInt(e.target.value) })}
                  style={{ width: '100%' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                  <span>Low</span>
                  <span>Medium</span>
                  <span>Critical</span>
                </div>
              </div>

              {/* Fuel type selector */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginRight: '0.5rem' }}>Fuel Type:</label>
                <select
                  value={fuelRequest.fuelType}
                  onChange={(e) => setFuelRequest({ ...fuelRequest, fuelType: e.target.value })}
                  style={{
                    padding: '0.5rem',
                    backgroundColor: 'var(--surface-2)',
                    border: '1px solid var(--border-default)',
                    borderRadius: '5px',
                    color: 'var(--text-primary)'
                  }}
                >
                  <option value="diesel">Diesel</option>
                  <option value="dyed_diesel">Dyed Diesel</option>
                  <option value="gasoline">Gasoline</option>
                </select>
              </div>

              {/* Fuel stops list */}
              <h3 style={{ color: 'var(--text-primary)', margin: '0 0 0.75rem 0' }}>Nearby Fuel Stops</h3>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                  Searching for fuel stops...
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {fuelStops.map((stop, index) => (
                    <div
                      key={stop.id}
                      style={{
                        backgroundColor: stop.isHomeTank ? 'var(--bg-tertiary)' : 'var(--surface-2)',
                        borderRadius: '8px',
                        padding: '1rem',
                        borderLeft: stop.isHomeTank ? '4px solid var(--accent-primary)' : '4px solid var(--accent-blue)',
                        cursor: 'pointer'
                      }}
                      onClick={() => selectFuelStop(stop)}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                            <span style={{ fontSize: '1.2rem' }}>
                              {stop.isHomeTank ? '🏠' : '⛽'}
                            </span>
                            <span style={{ color: 'var(--text-primary)', fontWeight: 'bold' }}>{stop.name}</span>
                            {stop.isHomeTank && (
                              <span style={{
                                backgroundColor: 'var(--btn-primary-bg)',
                                color: 'var(--text-primary)',
                                padding: '0.1rem 0.5rem',
                                borderRadius: '10px',
                                fontSize: '0.7rem'
                              }}>
                                HOME TANK
                              </span>
                            )}
                          </div>
                          <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{stop.address}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ color: 'var(--accent-primary)', fontWeight: 'bold' }}>
                            {stop.distance.toFixed(1)} km
                          </div>
                          {stop.pricePerLiter && (
                            <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                              ${stop.pricePerLiter.toFixed(2)}/L
                            </div>
                          )}
                        </div>
                      </div>
                      {stop.isHomeTank && stop.currentLevel && (
                        <div style={{ marginTop: '0.5rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-muted)', fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                            <span>Tank Level</span>
                            <span>{stop.currentLevel.toLocaleString()} / {stop.capacity.toLocaleString()} L</span>
                          </div>
                          <div style={{
                            height: '6px',
                            backgroundColor: 'var(--bg-primary)',
                            borderRadius: '3px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              height: '100%',
                              width: `${(stop.currentLevel / stop.capacity) * 100}%`,
                              backgroundColor: stop.currentLevel / stop.capacity > 0.3 ? 'var(--accent-primary)' : 'var(--color-danger)',
                              transition: 'width 0.3s ease'
                            }} />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : activeTab === 'log' ? (
            <div>
              <h3 style={{ color: 'var(--text-primary)', margin: '0 0 1rem 0' }}>Log Fuel Stop</h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ color: 'var(--text-muted)', fontSize: '0.9rem', display: 'block', marginBottom: '0.25rem' }}>
                    Station Name *
                  </label>
                  <input
                    type="text"
                    value={logForm.stationName}
                    onChange={(e) => setLogForm({ ...logForm, stationName: e.target.value })}
                    placeholder="e.g., Petro-Canada"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      backgroundColor: 'var(--surface-2)',
                      border: '1px solid var(--border-default)',
                      borderRadius: '5px',
                      color: 'var(--text-primary)'
                    }}
                  />
                </div>
                
                <div>
                  <label style={{ color: 'var(--text-muted)', fontSize: '0.9rem', display: 'block', marginBottom: '0.25rem' }}>
                    Fuel Type
                  </label>
                  <select
                    value={logForm.fuelType}
                    onChange={(e) => setLogForm({ ...logForm, fuelType: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      backgroundColor: 'var(--surface-2)',
                      border: '1px solid var(--border-default)',
                      borderRadius: '5px',
                      color: 'var(--text-primary)'
                    }}
                  >
                    <option value="diesel">Diesel</option>
                    <option value="dyed_diesel">Dyed Diesel</option>
                    <option value="gasoline">Gasoline</option>
                  </select>
                </div>
                
                <div>
                  <label style={{ color: 'var(--text-muted)', fontSize: '0.9rem', display: 'block', marginBottom: '0.25rem' }}>
                    Liters *
                  </label>
                  <input
                    type="number"
                    value={logForm.liters}
                    onChange={(e) => setLogForm({ ...logForm, liters: e.target.value })}
                    placeholder="e.g., 200"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      backgroundColor: 'var(--surface-2)',
                      border: '1px solid var(--border-default)',
                      borderRadius: '5px',
                      color: 'var(--text-primary)'
                    }}
                  />
                </div>
                
                <div>
                  <label style={{ color: 'var(--text-muted)', fontSize: '0.9rem', display: 'block', marginBottom: '0.25rem' }}>
                    Price per Liter (CAD)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={logForm.pricePerLiter}
                    onChange={(e) => setLogForm({ ...logForm, pricePerLiter: e.target.value })}
                    placeholder="e.g., 1.35"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      backgroundColor: 'var(--surface-2)',
                      border: '1px solid var(--border-default)',
                      borderRadius: '5px',
                      color: 'var(--text-primary)'
                    }}
                  />
                </div>
                
                <div>
                  <label style={{ color: 'var(--text-muted)', fontSize: '0.9rem', display: 'block', marginBottom: '0.25rem' }}>
                    Odometer (km)
                  </label>
                  <input
                    type="number"
                    value={logForm.odometerKm}
                    onChange={(e) => setLogForm({ ...logForm, odometerKm: e.target.value })}
                    placeholder={selectedTruck?.current_odometer_km?.toString() || ''}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      backgroundColor: 'var(--surface-2)',
                      border: '1px solid var(--border-default)',
                      borderRadius: '5px',
                      color: 'var(--text-primary)'
                    }}
                  />
                </div>
                
                <div>
                  <label style={{ color: 'var(--text-muted)', fontSize: '0.9rem', display: 'block', marginBottom: '0.25rem' }}>
                    Home Tank?
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-primary)', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={logForm.isHomeTank}
                      onChange={(e) => setLogForm({ ...logForm, isHomeTank: e.target.checked })}
                    />
                    Yes, this is a home tank fill
                  </label>
                </div>
              </div>
              
              <div style={{ marginTop: '1rem' }}>
                <label style={{ color: 'var(--text-muted)', fontSize: '0.9rem', display: 'block', marginBottom: '0.25rem' }}>
                  Notes
                </label>
                <textarea
                  value={logForm.notes}
                  onChange={(e) => setLogForm({ ...logForm, notes: e.target.value })}
                  placeholder="Any additional notes..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    backgroundColor: 'var(--surface-2)',
                    border: '1px solid var(--border-default)',
                    borderRadius: '5px',
                    color: 'var(--text-primary)',
                    resize: 'vertical'
                  }}
                />
              </div>
              
              {logForm.liters && logForm.pricePerLiter && (
                <div style={{
                  backgroundColor: 'var(--surface-2)',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginTop: '1rem',
                  textAlign: 'center'
                }}>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Total Cost</div>
                  <div style={{ color: 'var(--accent-primary)', fontSize: '1.5rem', fontWeight: 'bold' }}>
                    {formatCurrency(parseFloat(logForm.liters) * parseFloat(logForm.pricePerLiter))}
                  </div>
                </div>
              )}
              
              <button
                onClick={handleLogFuelStop}
                style={{
                  width: '100%',
                  padding: '1rem',
                  backgroundColor: 'var(--btn-primary-bg)',
                  color: 'var(--text-primary)',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  marginTop: '1rem'
                }}
              >
                Log Fuel Stop
              </button>
            </div>
          ) : activeTab === 'history' ? (
            <div>
              <h3 style={{ color: 'var(--text-primary)', margin: '0 0 1rem 0' }}>
                Fuel History - Truck {selectedTruck?.truck_number}
              </h3>
              
              {fuelHistory.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                  No fuel history for this truck
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--surface-2)' }}>
                      <th style={{ padding: '0.75rem', textAlign: 'left', color: 'var(--accent-primary)' }}>Date</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', color: 'var(--accent-primary)' }}>Station</th>
                      <th style={{ padding: '0.75rem', textAlign: 'right', color: 'var(--accent-primary)' }}>Liters</th>
                      <th style={{ padding: '0.75rem', textAlign: 'right', color: 'var(--accent-primary)' }}>Cost</th>
                      <th style={{ padding: '0.75rem', textAlign: 'right', color: 'var(--accent-primary)' }}>Odometer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fuelHistory.map(record => (
                      <tr key={record.id} style={{ borderBottom: '1px solid var(--surface-2)' }}>
                        <td style={{ padding: '0.75rem', color: 'var(--text-primary)' }}>
                          {new Date(record.created_at).toLocaleDateString()}
                        </td>
                        <td style={{ padding: '0.75rem', color: 'var(--text-primary)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            {record.is_home_tank && <span>🏠</span>}
                            {record.fuel_station_name}
                          </div>
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'right', color: 'var(--text-primary)' }}>
                          {record.liters} L
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'right', color: 'var(--accent-primary)' }}>
                          {formatCurrency(record.total_cost)}
                        </td>
                        <td style={{ padding: '0.75rem', textAlign: 'right', color: 'var(--text-muted)' }}>
                          {record.odometer_km?.toLocaleString()} km
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ) : (
            /* Home Tanks tab */
            <div>
              <h3 style={{ color: 'var(--text-primary)', margin: '0 0 1rem 0' }}>Home Fuel Tanks</h3>
              <p style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>
                Company-owned fuel tanks. Drivers should use these first when possible.
              </p>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {homeTanks.map(tank => (
                  <div
                    key={tank.id}
                    style={{
                      backgroundColor: 'var(--surface-2)',
                      borderRadius: '8px',
                      padding: '1rem',
                      borderLeft: '4px solid var(--accent-primary)'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                      <div>
                        <div style={{ color: 'var(--text-primary)', fontWeight: 'bold', marginBottom: '0.25rem' }}>
                          🏠 {tank.name}
                        </div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{tank.address}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ color: 'var(--accent-primary)', fontWeight: 'bold' }}>
                          ${tank.pricePerLiter.toFixed(2)}/L
                        </div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                          {tank.fuelTypes.join(', ')}
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: '0.25rem' }}>
                        <span>Tank Level</span>
                        <span>{tank.currentLevel.toLocaleString()} / {tank.capacity.toLocaleString()} L ({Math.round(tank.currentLevel / tank.capacity * 100)}%)</span>
                      </div>
                      <div style={{
                        height: '10px',
                        backgroundColor: 'var(--bg-primary)',
                        borderRadius: '5px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          height: '100%',
                          width: `${(tank.currentLevel / tank.capacity) * 100}%`,
                          backgroundColor: tank.currentLevel / tank.capacity > 0.3 ? 'var(--accent-primary)' : tank.currentLevel / tank.capacity > 0.1 ? 'var(--color-warning)' : 'var(--color-danger)',
                          transition: 'width 0.3s ease'
                        }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default FuelStopModal;
